import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  api,
  ApiError,
  type ModelPurpose,
  type ModelSettings,
  type ModelSettingsUpdate,
} from "@/lib/api";
import { MODEL_OFF } from "@/lib/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

// Sentinel option values — never sent to the backend; they express the
// omit-key semantics of the full-replace PUT (omitted purpose = follow chat,
// omitted transcription/embeddings/decisions = the Boo's built-in default).
// Off is not a sentinel: the literal "off" is sent.
const FOLLOW_CHAT = "__follow_chat__";
const DEFAULT = "__default__";

// Purposes with no chat fallback: omitting one from the PUT resets it to the
// built-in default, so every save must carry whatever is configured for them.
const SPECIALTY = [
  { key: "transcription", label: "Transcription", role: "transcription", off: "Off — no audio" },
  { key: "embeddings", label: "Embeddings", role: "embeddings", off: "Off — no memory search" },
  { key: "decisions", label: "Decisions", role: "decision", off: "Off — use the chat model" },
] as const;
type SpecialtyKey = (typeof SPECIALTY)[number]["key"];

const PURPOSES: Array<{ key: ModelPurpose; label: string }> = [
  { key: "chat", label: "Chat" },
  { key: "intents", label: "Intents" },
  { key: "subagents", label: "Subagents" },
  { key: "heartbeats", label: "Heartbeats" },
  { key: "workflow", label: "Workflow" },
  { key: "retrospect", label: "Retrospect" },
  { key: "indexing", label: "Indexing" },
];

type DraftKey = ModelPurpose | SpecialtyKey;

// The picker state the current server settings map onto: chat pins its
// effective model (the PUT requires an explicit chat), the rest surface
// their configured value (for specialty purposes: a model or "off") or the
// omit sentinel.
function baselineOf(settings: ModelSettings): Record<DraftKey, string> {
  const base = {} as Record<DraftKey, string>;
  for (const p of settings.purposes) {
    base[p.purpose] =
      p.purpose === "chat"
        ? (p.configured ?? p.effective)
        : (p.configured ?? FOLLOW_CHAT);
  }
  for (const sp of SPECIALTY) base[sp.key] = settings[sp.key]?.configured ?? DEFAULT;
  return base;
}

// What a specialty purpose does right now, and what Default would run.
function specialtyState(block: { configured: string | null; effective: string | null } | undefined) {
  const configured = block?.configured ?? null;
  const effective = block?.effective ?? null;
  if (configured === MODEL_OFF) return { status: "Off.", defaultDetail: undefined };
  if (configured) return { status: `Runs ${effective ?? configured}.`, defaultDetail: undefined };
  if (effective) return { status: `Default — currently ${effective}.`, defaultDetail: effective };
  // A Boo from before "off" runs nothing when nothing is configured.
  return { status: "Default — off on this version of Boo.", defaultDetail: "off on this version" };
}

function FileStateBadge({ state }: { state: ModelSettings["fileState"] }) {
  if (state === "ok") {
    return <Badge variant="outline" className="text-[10px]">settings file ok</Badge>;
  }
  if (state === "missing") {
    return (
      <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-600">
        no settings file — built-in defaults
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="text-[10px]">
      recovery mode — file invalid, running built-in defaults
    </Badge>
  );
}

function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start py-1.5">
      <div className="text-xs text-muted-foreground pt-2">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function ModelSettingsSection({ containerId }: { containerId: string }) {
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["admin-container-models", containerId],
    queryFn: () => api.admin.containerModels(containerId),
  });
  const catalogQuery = useQuery({
    queryKey: ["admin-container-model-catalog", containerId],
    queryFn: () => api.admin.containerModelCatalog(containerId),
    staleTime: 5 * 60 * 1000,
  });

  // Sparse override map on top of the server baseline; null = untouched.
  // The parent keys this component by container id, so no reset juggling.
  const [draft, setDraft] = useState<Partial<Record<DraftKey, string>> | null>(null);

  const save = useMutation({
    mutationFn: (update: ModelSettingsUpdate) =>
      api.admin.setContainerModels(containerId, update),
    onSuccess: (result) => {
      setDraft(null);
      if (!result.changed) {
        toast.info("Already in that state — nothing to change.");
        return;
      }
      toast.success(
        result.summary?.length
          ? `Applied: ${result.summary.join("; ")}. Boo is restarting to pick this up.`
          : "Model settings applied — Boo is restarting to pick them up.",
      );
      // The brain restarts in-process right after answering; give it a beat
      // before refetching so the refreshed view doesn't land mid-restart.
      window.setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: ["admin-container-models", containerId],
        });
      }, 4000);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Saving model settings failed");
    },
  });

  if (settingsQuery.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-2/3" />
      </div>
    );
  }
  if (settingsQuery.isError || !settingsQuery.data) {
    const err = settingsQuery.error;
    if (err instanceof ApiError && err.code === "brain_outdated") {
      return (
        <p className="text-xs text-muted-foreground py-1.5">
          This Boo runs a version from before model settings existed. Deploy a newer
          version above to manage its models.
        </p>
      );
    }
    return (
      <div className="flex items-center gap-3 py-1.5">
        <p className="text-xs text-destructive">
          {err instanceof Error ? err.message : "Failed to load model settings."}
        </p>
        <Button variant="outline" size="sm" onClick={() => settingsQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const settings = settingsQuery.data;
  const baseline = baselineOf(settings);
  const valueOf = (key: DraftKey) => draft?.[key] ?? baseline[key];
  const dirty =
    draft !== null &&
    (Object.keys(draft) as DraftKey[]).some((k) => draft[k] !== undefined && draft[k] !== baseline[k]);

  const catalog = catalogQuery.data;
  const chatModels = catalog?.models.filter((m) => m.role === "chat") ?? [];
  // A configured model that has since left the fleet must still render (and
  // survive a save of an unrelated purpose) — surface it as an extra option.
  const withCurrent = (models: typeof chatModels, current: string) =>
    current && !current.startsWith("__") && current !== MODEL_OFF && !models.some((m) => m.name === current)
      ? [...models, { name: current, role: "chat" as const, summary: "not in current catalog" }]
      : models;

  const setKey = (key: DraftKey, value: string) =>
    setDraft((d) => ({ ...(d ?? {}), [key]: value }));

  const describe = (value: string) =>
    value === FOLLOW_CHAT ? "use chat model" : value === DEFAULT ? "default" : value;
  const shownSpecialty = SPECIALTY.filter((sp) => sp.key === "transcription" || settings[sp.key]);
  const changes = ([...settings.purposes.map((p) => p.purpose), ...shownSpecialty.map((sp) => sp.key)] as DraftKey[])
    .filter((k) => valueOf(k) !== baseline[k])
    .map((k) => `${k}: ${describe(baseline[k])} → ${describe(valueOf(k))}`);

  // Every purpose the brain reported, not a fixed list: the PUT is a full
  // replace, so anything left out here is removed from the Boo's settings.
  const buildUpdate = (): ModelSettingsUpdate => {
    const update: ModelSettingsUpdate = { chat: valueOf("chat") };
    for (const { purpose } of settings.purposes) {
      if (purpose === "chat") continue;
      const v = valueOf(purpose);
      if (v !== FOLLOW_CHAT) update[purpose] = v;
    }
    for (const sp of shownSpecialty) {
      const v = valueOf(sp.key);
      if (v !== DEFAULT) update[sp.key] = v;
    }
    return update;
  };

  return (
    <div className="divide-y">
      <Row label="Settings file">
        <div className="pt-0.5 space-x-2">
          <FileStateBadge state={settings.fileState} />
          {catalog?.source === "baked-fallback" && (
            <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-600">
              live catalog unreachable — baked model list
            </Badge>
          )}
        </div>
      </Row>

      {settings.purposes.map((p) => {
        const label = PURPOSES.find((x) => x.key === p.purpose)?.label ?? p.purpose;
        const value = valueOf(p.purpose);
        return (
          <Row key={p.purpose} label={label}>
            <Select value={value} onValueChange={(v) => setKey(p.purpose, v)}>
              <SelectTrigger size="sm" className="w-full max-w-md font-mono text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {p.purpose !== "chat" && (
                  <SelectItem value={FOLLOW_CHAT}>
                    <span className="text-xs">Use chat model</span>
                  </SelectItem>
                )}
                {withCurrent(chatModels, value).map((m) => (
                  <SelectItem key={m.name} value={m.name}>
                    <span className="font-mono text-xs">{m.name}</span>
                    {m.summary && (
                      <span className="text-[11px] text-muted-foreground">{m.summary}</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              {p.purpose === "chat"
                ? p.source === "default"
                  ? "Built-in default — saving pins it explicitly."
                  : "Every other purpose falls back to this one."
                : p.source === "configured"
                  ? `Runs ${p.effective}.`
                  : p.source === "chat-fallback"
                    ? `Follows chat — currently ${p.effective}.`
                    : `Built-in default — currently ${p.effective}.`}
            </p>
          </Row>
        );
      })}

      {shownSpecialty.map((sp) => {
        const models = catalog?.models.filter((m) => m.role === sp.role) ?? [];
        const { status, defaultDetail } = specialtyState(settings[sp.key]);
        return (
          <Row key={sp.key} label={sp.label}>
            <Select value={valueOf(sp.key)} onValueChange={(v) => setKey(sp.key, v)}>
              <SelectTrigger size="sm" className="w-full max-w-md font-mono text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DEFAULT}>
                  <span className="text-xs">
                    Default
                    {defaultDetail && (
                      <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">· {defaultDetail}</span>
                    )}
                  </span>
                </SelectItem>
                <SelectItem value={MODEL_OFF}>
                  <span className="text-xs">{sp.off}</span>
                </SelectItem>
                {withCurrent(models, valueOf(sp.key)).map((m) => (
                  <SelectItem key={m.name} value={m.name}>
                    <span className="font-mono text-xs">{m.name}</span>
                    {m.summary && (
                      <span className="text-[11px] text-muted-foreground">{m.summary}</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">{status}</p>
          </Row>
        );
      })}

      {settings.threadModes.length > 0 && (
        <Row label="Thread modes">
          <div className="space-y-1 pt-1">
            {settings.threadModes.map((mode) => (
              <div key={mode.id} className="text-xs">
                <span className="font-medium">{mode.title}</span>{" "}
                <span className="font-mono text-[11px] text-muted-foreground break-all">
                  {mode.models.join(", ")}
                </span>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">
              Read-only: these preference lists ship with the Boo version. Who can use a
              mode is managed under thread flags, not here.
            </p>
          </div>
        </Row>
      )}

      <div className="flex items-center gap-3 py-3">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" disabled={!dirty || save.isPending}>
              {save.isPending ? "Applying…" : "Save models"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Apply model settings?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2">
                  <ul className="list-disc pl-5 font-mono text-xs space-y-0.5">
                    {changes.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <p>
                    Boo restarts immediately to pick up the change — it may be
                    unavailable for a few seconds and any in-flight work is interrupted.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => save.mutate(buildUpdate())}>
                Apply and restart
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {dirty && !save.isPending && (
          <span className="text-xs text-muted-foreground">Unsaved changes.</span>
        )}
        {save.isPending && (
          <span className="text-xs text-muted-foreground">
            Applying — Boo may be briefly unavailable.
          </span>
        )}
      </div>
    </div>
  );
}
